import {
  createCredentialBundle,
  CredentialType,
  validateComplexLoginMulti,
  validateHeaderKey,
  validateHeaderValue,
  validateStaticTokenFormData,
} from '@/sections/asset/components/tabs/credentials/credentialForms';
import {
  buildInitialData,
  WIZARD_STEPS as RECIPE_STEPS,
} from '@/sections/asset/components/tabs/credentials/RecipeWizard/wizardConfig';
import type { WizardCredentialData } from '@/sections/seeds/components/modals/AddSeedByTypeModal/AddSeedByTypeModal.types';

/**
 * Builds a fresh per-type credential bundle. Delegates to the shared
 * createCredentialBundle (every per-type form starts at its INITIAL constant),
 * renaming its `complexLoginMulti` field to the wizard's `complexLogin` key
 * (the shared bundle now carries the multi-identity model directly, seeded so
 * the first identity's loginUrl pre-fills from webAppUrl), and layers on the
 * wizard-only `recipeForm: null` field and a fresh `recipeDraft` seeded from
 * buildInitialData(webAppUrl). The active type defaults to BASIC for web
 * services and COMPLEX_LOGIN (Automated Authentication) for web apps.
 * Note: multi-seed adds snap back to STATIC_TOKEN via useConfigureCredential
 * (COMPLEX_LOGIN is gated to a single seed, so there is exactly one URL to
 * seed — D4). buildInitialData('') is safe (defaultLoginUrl('') returns '').
 */
export function createInitialCredentialData(
  isWebService: boolean,
  webAppUrl: string
): WizardCredentialData {
  // Build the shared bundle, then pick the per-type forms. The bundle's
  // multi-identity `complexLoginMulti` field is surfaced under the wizard's
  // `complexLogin` key (both are the same ComplexLoginMultiFormData, seeded
  // from webAppUrl) so the two hosts stay single-sourced.
  const bundle = createCredentialBundle(
    isWebService ? CredentialType.BASIC : CredentialType.COMPLEX_LOGIN,
    webAppUrl
  );
  return {
    selectedType: bundle.selectedType,
    basicForm: bundle.basicForm,
    recordingForm: bundle.recordingForm,
    bearerTokenForm: bundle.bearerTokenForm,
    apiKeyForm: bundle.apiKeyForm,
    staticTokenForm: bundle.staticTokenForm,
    complexLogin: bundle.complexLoginMulti,
    recipeForm: null, // wizard-only field; NOT part of the shared bundle
    recipeDraft: buildInitialData(webAppUrl), // intermediate recipe-builder state
  };
}

/**
 * Runs the FE defense-in-depth validators (P1-4) against the current bundle for
 * the active type and returns user-facing messages. Header rows are validated
 * for STATIC_TOKEN; the multi-identity set is validated for COMPLEX_LOGIN (every
 * identity must be valid AND there must be >=1 identity).
 *
 * Shared between {@link CredentialFormStep} (which gates Add Seeds on zero
 * errors) and the wizard step `validate` so display and gating stay
 * single-sourced. The COMPLEX_LOGIN per-identity messages are surfaced inline by
 * IdentitySetManager/IdentityEditor (via the MultiValidationResult), so this
 * helper only contributes the form-level gate for COMPLEX.
 */
export function collectFieldErrors(data: WizardCredentialData): string[] {
  const errors: string[] = [];

  if (data.selectedType === CredentialType.STATIC_TOKEN) {
    // Emptiness rule (F1): a bundle with no complete key+value pair produces no
    // credential, so it must be invalid. Reuse the canonical validator so the
    // exact filter + message can't drift.
    const emptinessError = validateStaticTokenFormData(data.staticTokenForm);
    if (emptinessError) errors.push(emptinessError);

    data.staticTokenForm.headers.forEach((header, index) => {
      if (header.key) {
        const keyError = validateHeaderKey(header.key);
        if (keyError) errors.push(`Header ${index + 1} name: ${keyError}`);
      }
      if (header.value) {
        const valueError = validateHeaderValue(header.value);
        if (valueError) errors.push(`Header ${index + 1} value: ${valueError}`);
      }
    });
  }

  // BASIC (F1): payload is null when BOTH username and password are empty
  // (useAddSeedSubmit credentialToPayload BASIC case). NOTE: this intentionally
  // diverges from validateBasicFormData, which requires BOTH fields — the
  // payload needs only ONE, so gating on the validator would over-reject a
  // username-only Basic credential that produces a valid payload.
  if (data.selectedType === CredentialType.BASIC) {
    if (!data.basicForm.username && !data.basicForm.password) {
      errors.push('Enter a username or password.');
    }
  }

  // BEARER_TOKEN (F1): payload is null when no token AND not dynamic
  // (credentialToPayload BEARER_TOKEN case). Mirrors that exact condition
  // rather than validateBearerTokenFormData, which also requires a label and
  // full dynamic config — stricter than the payload-null contract.
  if (data.selectedType === CredentialType.BEARER_TOKEN) {
    if (!data.bearerTokenForm.token && !data.bearerTokenForm.isDynamic) {
      errors.push('Enter a bearer token or enable dynamic token retrieval.');
    }
  }

  // API_KEY (F1): payload is null when no apiKey AND not dynamic
  // (credentialToPayload API_KEY case). Same payload-null-only contract.
  if (data.selectedType === CredentialType.API_KEY) {
    if (!data.apiKeyForm.apiKey && !data.apiKeyForm.isDynamic) {
      errors.push('Enter an API key or enable dynamic token retrieval.');
    }
  }

  if (data.selectedType === CredentialType.COMPLEX_LOGIN) {
    const result = validateComplexLoginMulti(data.complexLogin);
    if (!result.ok) {
      // Form-level gate: any invalid/incomplete identity blocks Add Seeds. The
      // per-identity detail is rendered inline by IdentitySetManager, so a
      // single generic message here is enough to keep the gate non-empty.
      errors.push(result.formError ?? 'Complete all identities before adding.');
    }
  }

  if (data.selectedType === CredentialType.RECIPE) {
    // Reuse the RecipeWizard step validators (single source of truth) so a
    // label-only / partial recipeDraft is reported incomplete. Indices map to
    // RECIPE_STEPS positions: 0=Overview(label), 1=Inputs, 2=Request(url),
    // 3=Session. Index 4 (Review) is always-valid, so it is intentionally
    // skipped. Mirrors recipeValidate() in wizardConfig.ts.
    const RECIPE_STEP_MESSAGES = [
      'Name this login.',
      'Add at least one login credential (a name and a value).',
      'Enter the login URL.',
      'Complete the session capture details.',
    ];
    RECIPE_STEP_MESSAGES.forEach((message, i) => {
      const valid = RECIPE_STEPS[i].validate?.(data.recipeDraft) ?? true;
      if (!valid) errors.push(message);
    });
  }

  return errors;
}
